import React from 'react'
import type { TFunction } from 'i18next'
import { Boxes, Hammer, HelpCircle, KeyRound, Receipt, Sparkles, Wifi, Wrench, type LucideIcon } from 'lucide-react'
import { sdk } from '@/lib/sdk'
import { useCommunity } from '@/lib/CommunityContext'
import { useLocale } from '@/lib/LocaleContext'
import { TEAM_ROLES } from '@/lib/permissions'
import { uploadMedia } from '@/components/features/media/mediaUpload'
import type {
  SupportBusinessHours,
  SupportCategory,
  SupportKind,
  SupportModuleConfig,
  SupportPerson,
  SupportPriority,
  SupportSelection,
  SupportSlaCategory,
  SupportStatus,
  SupportTarget
} from '@/types/support'
import { SUPPORT_CATEGORIES } from '@/types/support'

export { apiFieldErrors, apiMessage, apiStatus, spacePathOf, spaceTitleOf } from '../parcels/parcelUtils'

/** The API accepts five photos per message and twenty per ticket. */
export const MAX_PHOTOS = 5
export const MAX_TITLE = 140
/** The API refuses more than 10,000 characters; the form offers 2,000 (proposal section 7.1). */
export const MAX_DESCRIPTION = 2000
export const MAX_BODY = 2000
export const MAX_LOCATION = 120
export const MAX_HINT = 200

export const KIND_ICON: Record<SupportKind, LucideIcon> = { MAINTENANCE: Wrench, REQUEST: HelpCircle }

export const CATEGORY_ICON: Record<SupportCategory, LucideIcon> = {
  FACILITIES: Hammer,
  CLEANING: Sparkles,
  EQUIPMENT: Boxes,
  INTERNET: Wifi,
  ACCESS: KeyRound,
  BILLING: Receipt,
  OTHER: HelpCircle
}

export const STATUS_VARIANT: Record<SupportStatus, 'warning' | 'success' | 'info' | 'default' | 'danger'> = {
  OPEN: 'warning',
  IN_PROGRESS: 'info',
  WAITING_REQUESTER: 'danger',
  RESOLVED: 'success',
  CANCELED: 'default'
}

export const PRIORITY_VARIANT: Record<SupportPriority, 'warning' | 'success' | 'info' | 'default' | 'danger'> = {
  LOW: 'default',
  NORMAL: 'default',
  HIGH: 'warning',
  URGENT: 'danger'
}

export const isOpenStatus = (status: SupportStatus): boolean => status !== 'RESOLVED' && status !== 'CANCELED'

export const personName = (person?: SupportPerson | null): string => person?.name || person?.displayName || ''
export const personAvatar = (person?: SupportPerson | null): string | null => person?.avatar || person?.avatarUrl || null

export const newRequestId = () => (typeof crypto !== 'undefined' && 'randomUUID' in crypto ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(16).slice(2)}`)

/** Uploads one photo through the hosted-media pipeline and resolves with the confirmed media id. */
export const uploadSupportPhoto = async (spaceId: string, file: File, opts: { onProgress?: (p: number) => void; signal?: AbortSignal } = {}): Promise<string> => {
  const { asset } = await uploadMedia({ purpose: 'supportPhoto', spaceId, file, ...opts })
  return asset.id
}

/** An upload that never got attached: the file and the asset go away. Failures are not worth a message. */
export const discardUnattached = (mediaId: string) => { void sdk.media.delete(mediaId).catch(() => undefined) }

/** "2 hours ago", in the reader's language; a minute is the smallest step. */
export const useAgo = () => {
  const { currentLocale } = useLocale()
  const formatter = React.useMemo(() => new Intl.RelativeTimeFormat(currentLocale, { numeric: 'auto' }), [currentLocale])
  return React.useCallback((iso?: string | null, now = Date.now()): string => {
    if (!iso) return ''
    const minutes = Math.round((new Date(iso).getTime() - now) / 60000)
    const abs = Math.abs(minutes)
    if (abs < 60) return formatter.format(minutes, 'minute')
    if (abs < 60 * 24) return formatter.format(Math.round(minutes / 60), 'hour')
    return formatter.format(Math.round(minutes / 1440), 'day')
  }, [formatter])
}

export interface TeamOption { id: string; name: string; avatar: string | null; role: string }

export interface MemberRow { memberId: string; displayName?: string | null; fullName?: string | null; email?: string | null; avatarUrl?: string | null; role: string }

export const toTeamOption = (p: MemberRow): TeamOption => ({
  id: p.memberId,
  name: p.displayName || p.fullName || p.email || '',
  avatar: p.avatarUrl || null,
  role: p.role
})

/**
 * The team (owner, admin, moderator, finance) of the community, loaded once when `enabled`.
 * The server decides who may answer each category and refuses anyone else on assign.
 */
export const useTeamOptions = (enabled: boolean) => {
  const { community } = useCommunity()
  const [options, setOptions] = React.useState<TeamOption[] | null>(null)
  const [failed, setFailed] = React.useState(false)
  const communityId = community?.id

  React.useEffect(() => {
    if (!enabled || !communityId || options !== null) return
    let alive = true
    Promise.all(TEAM_ROLES.map(async (role) => {
      const result = await sdk.members.list({ role, limit: 100 }) as MemberRow[] | { items?: MemberRow[] }
      const rows: MemberRow[] = Array.isArray(result) ? result : result?.items ?? []
      return rows.map(toTeamOption)
    }))
      .then((lists) => {
        if (!alive) return
        const seen = new Set<string>()
        setOptions(lists.flat().filter((o) => o.id && !seen.has(o.id) && seen.add(o.id)).sort((a, b) => a.name.localeCompare(b.name)))
      })
      .catch(() => { if (alive) { setOptions([]); setFailed(true) } })
    return () => { alive = false }
  }, [enabled, communityId, options])

  return { options: options ?? [], loading: enabled && options === null, failed }
}

/* ------------------------------------------------------------------ */
/* The space setup (`Space.moduleConfig.support`)                       */
/* ------------------------------------------------------------------ */

export const DEFAULT_TARGETS: SupportSlaCategory = {
  firstResponse: { value: 4, unit: 'hours' },
  resolution: { value: 1, unit: 'days' }
}

export const DEFAULT_BUSINESS_HOURS: SupportBusinessHours = { days: [1, 2, 3, 4, 5], start: '09:00', end: '18:00' }

export const emptySelection = (): SupportSelection => ({ tags: [], groupIds: [], memberIds: [] })

const allCategoriesSla = (stored?: Partial<Record<SupportCategory, Partial<SupportSlaCategory>>>): Record<SupportCategory, SupportSlaCategory> => {
  const out = {} as Record<SupportCategory, SupportSlaCategory>
  for (const category of SUPPORT_CATEGORIES) {
    const own = stored?.[category]
    out[category] = {
      firstResponse: { ...(own?.firstResponse ?? DEFAULT_TARGETS.firstResponse) },
      resolution: { ...(own?.resolution ?? DEFAULT_TARGETS.resolution) }
    }
  }
  return out
}

export const defaultSupportConfig = (): SupportModuleConfig => ({
  allowUrgentFlag: true,
  maxOpenPerMember: 10,
  locationHint: '',
  defaultAssigneeId: null,
  responders: { ...emptySelection(), byCategory: {} },
  sla: { enabled: true, businessHours: { ...DEFAULT_BUSINESS_HOURS, days: [...DEFAULT_BUSINESS_HOURS.days] }, categories: allCategoriesSla() }
})

/** The stored object (any of its keys may be missing) over the defaults. */
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- the stored object is read key by key and checked below
export const supportConfigFrom = (stored: Record<string, any> | null | undefined): SupportModuleConfig => {
  const base = defaultSupportConfig()
  if (!stored) return base
  const byCategory: SupportModuleConfig['responders']['byCategory'] = {}
  for (const category of SUPPORT_CATEGORIES) {
    const sel = stored.responders?.byCategory?.[category]
    if (sel) byCategory[category] = { ...emptySelection(), ...sel }
  }
  return {
    allowUrgentFlag: typeof stored.allowUrgentFlag === 'boolean' ? stored.allowUrgentFlag : base.allowUrgentFlag,
    maxOpenPerMember: Number.isInteger(stored.maxOpenPerMember) ? stored.maxOpenPerMember : base.maxOpenPerMember,
    locationHint: typeof stored.locationHint === 'string' ? stored.locationHint : '',
    defaultAssigneeId: typeof stored.defaultAssigneeId === 'string' ? stored.defaultAssigneeId : null,
    responders: { ...emptySelection(), ...(stored.responders ?? {}), byCategory },
    sla: {
      enabled: stored.sla?.enabled !== false,
      businessHours: stored.sla?.businessHours?.days?.length ? { ...DEFAULT_BUSINESS_HOURS, ...stored.sla.businessHours } : base.sla.businessHours,
      categories: allCategoriesSla(stored.sla?.categories)
    }
  }
}

/** The slice that goes to the API. A category with an empty own selection is dropped: it follows the space. */
export const toSupportInput = (config: SupportModuleConfig): SupportModuleConfig => {
  const byCategory: SupportModuleConfig['responders']['byCategory'] = {}
  for (const category of SUPPORT_CATEGORIES) {
    const sel = config.responders.byCategory[category]
    if (sel && (sel.tags.length || sel.groupIds.length || sel.memberIds.length)) byCategory[category] = sel
  }
  return {
    allowUrgentFlag: config.allowUrgentFlag,
    maxOpenPerMember: config.maxOpenPerMember,
    locationHint: config.locationHint.trim(),
    defaultAssigneeId: config.defaultAssigneeId || null,
    responders: { tags: config.responders.tags, groupIds: config.responders.groupIds, memberIds: config.responders.memberIds, byCategory },
    sla: config.sla
  }
}

export interface SupportConfigErrors {
  maxOpenPerMember?: string
  locationHint?: string
  sla?: string
  businessHours?: string
  [key: string]: string | undefined
}

const targetOk = (target: SupportTarget) => Number.isInteger(target.value) && target.value >= 1 && target.value <= (target.unit === 'days' ? 365 : 8760)

export const validateSupportConfig = (config: SupportModuleConfig, t: TFunction): SupportConfigErrors | null => {
  const errors: SupportConfigErrors = {}
  if (!Number.isInteger(config.maxOpenPerMember) || config.maxOpenPerMember < 0 || config.maxOpenPerMember > 1000) {
    errors.maxOpenPerMember = t('support:config.errors.maxOpen', { min: 0, max: 1000 })
  }
  if ([...config.locationHint].length > MAX_HINT) errors.locationHint = t('support:config.errors.hint', { max: MAX_HINT })
  if (config.sla.enabled) {
    const h = config.sla.businessHours
    if (!h.days.length || !/^\d{2}:\d{2}$/.test(h.start) || !/^\d{2}:\d{2}$/.test(h.end) || h.start >= h.end) errors.businessHours = t('support:config.errors.hours')
    for (const category of SUPPORT_CATEGORIES) {
      const own = config.sla.categories[category]
      if (!targetOk(own.firstResponse) || !targetOk(own.resolution)) { errors.sla = t('support:config.errors.sla'); break }
    }
  }
  return Object.keys(errors).length ? errors : null
}

interface ApiError { response?: { data?: { errors?: Array<{ param?: string; message?: string }> } } }

/** `{ param: 'moduleConfig.support.maxOpenPerMember', message }` into per-field messages. */
export const supportConfigApiErrors = (err: unknown): SupportConfigErrors | null => {
  const list = (err as ApiError | null)?.response?.data?.errors ?? []
  const out: SupportConfigErrors = {}
  for (const e of list) {
    const match = e.param?.match(/^moduleConfig\.support\.(\w+)/)
    if (match && e.message) out[match[1]] = e.message
  }
  return Object.keys(out).length ? out : null
}
